import clsx from "clsx";
import { generateFileURL } from "~/utils/file/generateFileURL";
import {
  companyMemberDBObject,
  type CompanyMemberDB,
} from "~/database/company_members.model";
import {
  useState,
  useRef,
  useEffect,
} from "react";
import {
  useFetcher,
  Link,
} from "@remix-run/react";
import { useUpload } from "~/utils/file/uploadFileToR2";
import toast from "react-hot-toast";
import {
  array,
  object,
  optional,
  string,
} from "valibot";
import {
  getInputProps,
  useForm,
} from "@conform-to/react";
import { parseWithValibot } from "conform-to-valibot";
import { getFormElement } from "node_modules/@conform-to/react/integrations";

import type { action } from "~/routes/api.company-members.update/route";
import ErrorDisplay from "../Common/ErrorDisplay";
interface CompanyMembersListProps {
  members: CompanyMemberDB[];
  basePath?: string;
  isEditable?: boolean;
  userId?: string;
  onEdit?: (members: CompanyMemberDB[]) => void;
}

export const MembersEditItemSchema = object({
  members: array(
    object({
      name: companyMemberDBObject.entries.name,
      position:
        companyMemberDBObject.entries.position,
      profile_image_url:
        companyMemberDBObject.entries
          .profile_image_url,
      id: optional(string()),
    }),
  ),
});

export default function CompanyMembersList({
  members,
  basePath,
  isEditable = false,
  userId,
  onEdit,
}: CompanyMembersListProps) {
  const [isEditMode, setIsEditMode] =
    useState(false);
  /* const [editingMembers, setEditingMembers] =
    useState<CompanyMemberDB[]>(members); */
  const fetcher = useFetcher<typeof action>();
  const uploadingIndex = useRef<number | null>(
    null,
  );

  const [form, fields] = useForm({
    defaultValue: {
      members: members.map((member) => ({
        name: member.name,
        position: member.position,
        profile_image_url:
          member.profile_image_url,
        id: member.id,
      })),
    },
    onValidate: ({ formData }) =>
      parseWithValibot(formData, {
        schema: MembersEditItemSchema,
      }),

    onSubmit: (e) => {
      const formElm = getFormElement(form.id);
      fetcher.submit(formElm, {
        method: "post",
        action: "/api/company-members/update",
      });
      e.preventDefault();
    },
  });

  useEffect(() => {
    if (!fetcher.data) return;
    if (fetcher.data?.success) {
      toast.success("保存しました", {
        duration: 4000,
      });
      setIsEditMode(false);
    } else {
      toast.error("エラーが発生しました。", {
        duration: 4000,
      });
    }
  }, [fetcher.data, fetcher.data?.success]);

  const editingMembers =
    fields.members.getFieldList();

  // 画像アップロード用のuseUploadフック
  const { imageSelect } = useUpload(
    (path: string) => {
      if (uploadingIndex.current !== null) {
        const index = uploadingIndex.current;
        if (
          fields.members.value &&
          Array.isArray(fields.members.value)
        ) {
          const updated = [
            ...fields.members.value,
          ];

          updated[index] = {
            ...updated[index],
            profile_image_url: path,
          };
          form.update({
            name: `members`,
            value: updated,
          });
          uploadingIndex.current = null;
        }
      }
    },
  );

  const handleImageUpload = async (
    index: number,
    file: File,
  ) => {
    // ファイルタイプチェック
    const acceptType = [
      "image/png",
      "image/jpg",
      "image/jpeg",
      "image/gif",
      "image/webp",
    ];
    if (!acceptType.includes(file.type)) {
      toast.error(
        "画像ファイルを選択してください。",
        { duration: 4000 },
      );
      return;
    }

    // ファイルサイズチェック（5MB制限）
    const maxSize = 1024 * 1024 * 5;
    if (file.size > maxSize) {
      toast.error(
        "5MB以下の画像ファイルを選択してください。",
        { duration: 4000 },
      );
      return;
    }

    uploadingIndex.current = index;
    imageSelect(file);
  };

  const handleCancel = () => {
    //setEditingMembers(members);
    setIsEditMode(false);
  };

  return (
    <div className={clsx("mt-40")}>
      <form
        id={form.id}
        onSubmit={form.onSubmit}
        className={clsx(
          "max-md:w-full",
          "grid",
          "grid-cols-[auto_1fr_auto]",
          "items-start",
          "max-md:flex",
          "max-md:flex-col",
        )}
      >
        <div
          className={clsx(
            "mb-24 flex items-center justify-between",
            "w-340",
          )}
        >
          <h3
            className={clsx(
              "text-40 font-bold",
              "max-md:text-24",
            )}
          >
            MEMBER
          </h3>
        </div>

        {fetcher.data?.error && (
          <ErrorDisplay
            error={fetcher.data?.error}
          ></ErrorDisplay>
        )}

        <div
          className={clsx(
            !(
              members.length === 0 && !isEditMode
            ) && "grid grid-cols-4 gap-24",
            "max-md:grid-cols-2",
            "max-md:gap-16",
            "max-md:flex",
            "max-md:flex-col",
          )}
        >
          {members.length === 0 &&
            !isEditMode && (
              <div
                className={clsx(
                  "text-center",
                  "py-40",
                  "text-gray-400",
                  "text-14",
                )}
              >
                メンバー情報が登録されていません
              </div>
            )}
          {!isEditMode &&
            members
              .slice(0, 4)
              .map((member, index) => (
                <div
                  key={member.id}
                  className={clsx("relative")}
                >
                  <div
                    className={clsx(
                      "flex flex-col items-center text-center",
                      "relative",
                      "z-[1]",
                    )}
                  >
                    <div
                      className={clsx(
                        "w-150",
                        "h-150",
                        "rounded-full",
                        "bg-gray-100",
                        "flex-shrink-0",
                        "relative",
                        "overflow-hidden",
                        "mb-16",
                      )}
                    >
                      {member.profile_image_url && (
                        <img
                          src={generateFileURL(
                            member.profile_image_url,
                            undefined,
                            basePath,
                          )}
                          alt={member.name}
                          className={clsx(
                            "w-full",
                            "h-full",
                            "object-cover",
                          )}
                        />
                      )}
                    </div>
                    <>
                      <div
                        className={clsx(
                          "text-20",
                          "font-bold",
                          "mb-2",
                        )}
                      >
                        {member.name}
                      </div>
                      {member.position && (
                        <div
                          className={clsx(
                            "text-12",
                            "text-gray-500",
                          )}
                        >
                          {member.position}
                        </div>
                      )}
                    </>
                  </div>
                </div>
              ))}

          {isEditMode &&
            editingMembers.map(
              (member, index) => (
                <div
                  key={member.id}
                  className={clsx(
                    "relative",
                    isEditMode &&
                      "rounded-lg border border-gray-200 bg-gray-50 p-16",
                  )}
                >
                  {isEditMode && (
                    <button
                      {...form.remove.getButtonProps(
                        {
                          name: fields.members
                            .name,
                          index: index,
                        },
                      )}
                      className={clsx(
                        "absolute",
                        "top-8",
                        "right-8",
                        "w-20",
                        "h-20",
                        "flex",
                        "items-center",
                        "justify-center",
                        "bg-red-500",
                        "text-white",
                        "rounded-full",
                        "text-12",
                        "z-[2]",
                      )}
                    >
                      ×
                    </button>
                  )}

                  <div
                    className={clsx(
                      "flex flex-col items-center text-center",
                      "relative",
                      "z-[1]",
                    )}
                  >
                    <div
                      className={clsx(
                        "w-150",
                        "h-150",
                        "rounded-full",
                        "bg-gray-100",
                        "flex-shrink-0",
                        "relative",
                        "overflow-hidden",
                        "mb-16",
                      )}
                    >
                      {member.value
                        ?.profile_image_url && (
                        <img
                          src={generateFileURL(
                            member.getFieldset()
                              .profile_image_url
                              ?.value ?? "",

                            undefined,
                            basePath,
                          )}
                          alt={member.name}
                          className={clsx(
                            "w-full",
                            "h-full",
                            "object-cover",
                          )}
                        />
                      )}
                      {isEditMode && (
                        <>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file =
                                e.target
                                  .files?.[0];
                              if (file) {
                                handleImageUpload(
                                  index,
                                  file,
                                );
                              }
                            }}
                            className="hidden"
                            id={`member-image-${member.id}`}
                          />
                          <label
                            htmlFor={`member-image-${member.id}`}
                            className={clsx(
                              "absolute",
                              "inset-0",
                              "bg-black",
                              "bg-opacity-50",
                              "text-white",
                              "text-10",
                              "flex",
                              "items-center",
                              "justify-center",
                              "cursor-pointer",
                            )}
                          >
                            変更
                          </label>
                        </>
                      )}
                    </div>

                    <input
                      {...getInputProps(
                        member.getFieldset()
                          .profile_image_url,
                        {
                          type: "hidden",
                        },
                      )}
                    />

                    <input
                      {...getInputProps(
                        member.getFieldset().id,
                        {
                          type: "hidden",
                        },
                      )}
                    />
                    <>
                      <input
                        /* onChange={(e) =>
                        handleUpdateMember(
                          index,
                          "name",
                          e.target.value,
                        )
                      } */

                        {...getInputProps(
                          member.getFieldset()
                            .name,
                          {
                            type: "text",
                          },
                        )}
                        placeholder="名前"
                        className={clsx(
                          "w-full",
                          "text-14",
                          "font-bold",
                          "mb-4",
                          "p-4",
                          "border",
                          "rounded",
                          "text-center",
                        )}
                      />
                      <input
                        {...getInputProps(
                          member.getFieldset()
                            .position,
                          {
                            type: "text",
                          },
                        )}
                        /* defaultValue={
                      member.value?.position
                    } */
                        /* onChange={(e) =>
                        handleUpdateMember(
                          index,
                          "position",
                          e.target.value,
                        )
                      } */
                        /* name={
                      member.getFieldset()
                        .position.name
                    } */
                        placeholder="役職"
                        className={clsx(
                          "w-full",
                          "text-12",
                          "text-gray-600",
                          "p-4",
                          "border",
                          "rounded",
                          "text-center",
                        )}
                      />
                    </>
                  </div>
                </div>
              ),
            )}

          {isEditMode && (
            <button
              className={clsx(
                "border",
                "border-dashed",
                "border-gray-300",
                "p-16",
                "rounded-lg",
                "flex",
                "items-center",
                "justify-center",
                "hover:bg-gray-50",
                "min-h-[120px]",
              )}
              {...form.insert.getButtonProps({
                name: fields.members.name,
              })}
            >
              <span
                className={clsx(
                  "text-24",
                  "text-gray-400",
                )}
              >
                +
              </span>
            </button>
          )}
        </div>

        {isEditMode && (
          <div
            className={clsx(
              "flex",
              "gap-12",
              "justify-end",
            )}
          >
            <button
              onClick={handleCancel}
              className={clsx(
                "px-20",
                "py-8",
                "border",
                "rounded-full",
                "text-12",
              )}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className={clsx(
                "px-20",
                "py-8",
                "bg-black",
                "text-white",
                "rounded-full",
                "text-12",
              )}
            >
              保存
            </button>
          </div>
        )}

        {isEditable && !isEditMode && (
          <div
            className={clsx(
              "w-340",
              "text-right",
            )}
          >
            <button
              onClick={() => setIsEditMode(true)}
              className={clsx(
                "text-12",
                "px-16",
                "py-6",
                "border",
                "border-gray-300",
                "rounded-full",
                "hover:bg-gray-50",
                "transition-colors",
              )}
            >
              {members.length === 0
                ? "追加"
                : "編集"}
            </button>
          </div>
        )}
      </form>
      <div className={clsx()}>
        {!isEditMode && members.length > 0 && (
          <div
            className={clsx(
              "text-center",
              "mt-32",
            )}
          >
            <Link
              to={`/user/${userId}/members`}
              className={clsx(
                "inline-block",
                "px-32",
                "py-12",
                "border",
                "border-gray-300",
                "rounded-full",
                "text-14",
                "hover:bg-gray-50",
                "transition-colors",
              )}
            >
              全てのメンバーを見る（
              {members.length}
              人）
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
